/**
 * MotionDetectionAdapter — phase 2 detection: motion, not the ball.
 *
 * Per TASK_PACKET.md the scoreboard only needs two cheap facts:
 *   - "rally over"  = all motion stops at once,
 *   - "who won"     = who walks to the service line (the next serve).
 * The next serve is the CHECK for the previous rally, which is exactly the
 * design already in the engine: a SERVE_DETECTED validates an already-ended,
 * unprocessed rally.
 *
 * This file is a NEW producer of domain events. The engine is NOT made
 * camera-aware: it never imports this file; this file only imports the engine's
 * domain types. The dependency runs one way only.
 */

import type { DomainEvent, PhysicalSide, TeamId } from "../core/types";
import type { CameraAdapter, CameraFrameStats, DetectionAdapter, DetectionConfig, DetectionEventSink, FieldCalibration } from "./types";
import { PerformanceTracker } from "./perf";

/* ------------------------------------------------------------------ */
/* Tuning                                                              */
/* ------------------------------------------------------------------ */

export interface MotionTuning {
  /** Processing width; height scales with the video aspect ratio. */
  processWidth: number;
  /** Motion energy above this counts as "activity". */
  activeThreshold: number;
  /** Motion energy below this counts as "all stopped". */
  quietThreshold: number;
  /** How long motion must stay quiet before a rally is declared over. */
  endDebounceMs: number;
  /** Minimum rally length before RALLY_ENDED may fire (kills serve-gap noise). */
  minRallyMs: number;
  /** Minimum gap between two serve detections (flicker suppression). */
  minServeGapMs: number;
  /** Fraction (0..1) from the top where the "service area" (baseline) begins. */
  serviceBandTop: number;
}

export const DEFAULT_MOTION_TUNING: MotionTuning = {
  processWidth: 160,
  // Tuned on testbeeld/rally-kort.mp4 (proxy, behind the baseline): active
  // play sits at ~1.5–3.2 mean-abs-diff, and "all stopped" is exactly 0.
  activeThreshold: 1.2,
  quietThreshold: 0.7,
  endDebounceMs: 1500,
  minRallyMs: 1200,
  minServeGapMs: 2500,
  serviceBandTop: 0.55,
};

type DetectorState = "idle" | "rally" | "ended";

export interface MotionStatus {
  state: DetectorState;
  /** Dutch, human-readable status. */
  label: string;
  motionEnergy: number;
  serveSide: PhysicalSide | null;
  lastServeConfidence: number;
  rallyCount: number;
  endCount: number;
  missedEnds: number;
}

/* ------------------------------------------------------------------ */
/* Adapter                                                             */
/* ------------------------------------------------------------------ */

export class MotionDetectionAdapter implements DetectionAdapter {
  id = "motion";

  private cfg: DetectionConfig = {
    processWidth: 640,
    targetFps: 15,
    clipAroundEndMs: 8000,
    useVideoFrameCallback: true,
    highConfidence: 0.88,
    mediumConfidence: 0.65,
  };

  private tuning = { ...DEFAULT_MOTION_TUNING };
  private camera: CameraAdapter | null = null;
  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private prevGray: Float32Array | null = null;
  private sink: DetectionEventSink | null = null;
  private cal: FieldCalibration | null = null;
  private tracker = new PerformanceTracker();
  private timer: number | null = null;

  private state: DetectorState = "idle";
  private seq = 0;
  private lastEventTs = 0;
  private quietSince = 0;
  private rallyStartTs = 0;
  private lastRallyDetectorId: string | null = null;
  private lastServeSide: PhysicalSide | null = null;
  private lastServeConfidence = 0;
  private motionEnergy = 0;
  private approachLeft = 0;
  private approachRight = 0;
  private rallyCount = 0;
  private endCount = 0;
  private missedEnds = 0;

  private trackEndedHandler: (() => void) | null = null;
  private cameraLostHandler: (() => void) | null = null;

  isSupported(): boolean {
    return true;
  }

  describe(): string {
    return "Motion-detector (rally-einde = beweging stopt; winnaar = wie gaat serveren)";
  }

  configure(cfg: Partial<DetectionConfig>): void {
    this.cfg = { ...this.cfg, ...cfg };
  }

  setTuning(t: Partial<MotionTuning>): void {
    this.tuning = { ...this.tuning, ...t };
  }

  setCameraLostHandler(fn: (() => void) | null): void {
    this.cameraLostHandler = fn;
  }

  async attach(camera: CameraAdapter): Promise<void> {
    this.camera = camera;
    const res = await camera.start();
    this.setupVideo(res.video, res.stream);
  }

  /** Bind to an ALREADY-started video/stream (shared source in the app UI). */
  attachVideo(video: HTMLVideoElement, stream: MediaStream): void {
    this.camera = null;
    this.setupVideo(video, stream);
  }

  private setupVideo(video: HTMLVideoElement, stream: MediaStream): void {
    this.video = video;
    this.stream = stream;
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d", { willReadFrequently: true });
    this.prevGray = null;
    this.state = "idle";
    this.quietSince = 0;
    this.rallyStartTs = 0;
    this.lastRallyDetectorId = null;
    this.approachLeft = 0;
    this.approachRight = 0;
    this.rallyCount = 0;
    this.endCount = 0;
    this.missedEnds = 0;

    // Camera lost mid-match must never corrupt the log: stop emitting and
    // tell the app so it can show a Dutch status. The event log is untouched.
    this.trackEndedHandler = () => this.notifyCameraLost();
    this.stream?.getVideoTracks().forEach((t) => {
      t.addEventListener("ended", this.trackEndedHandler!);
    });
  }

  start(sink: DetectionEventSink): void {
    this.sink = sink;
    if (!this.video) return;
    this.state = "idle";
    this.quietSince = 0;
    this.lastEventTs = performance.now();
    this.tracker.reset();
    // setInterval (niet rVFC/rAF): fire-able in headless Chrome and in the
    // background; motion is a low-frequency signal so 15 Hz sampling is ample.
    const intervalMs = Math.max(1, Math.round(1000 / this.cfg.targetFps));
    this.stopTimer();
    this.timer = window.setInterval(() => this.tick(), intervalMs);
  }

  stop(): void {
    this.stopTimer();
    if (this.stream) {
      this.stream.getVideoTracks().forEach((t) => {
        if (this.trackEndedHandler) t.removeEventListener("ended", this.trackEndedHandler);
      });
    }
    this.trackEndedHandler = null;
    this.camera?.stop();
    this.video = null;
    this.stream = null;
    this.prevGray = null;
    this.sink = null;
  }

  private stopTimer(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  private tick(): void {
    const t0 = performance.now();
    this.onFrame(t0);
    this.tracker.frame(t0, performance.now() - t0);
  }

  setFieldCalibration(cal: FieldCalibration | null): void {
    this.cal = cal;
  }

  get status(): MotionStatus {
    const label =
      this.state === "idle" ? "Wacht op opslag"
      : this.state === "rally" ? "Rally actief"
      : "Rally geëindigd — volgende opslag bevestigt";
    return {
      state: this.state,
      label,
      motionEnergy: this.motionEnergy,
      serveSide: this.lastServeSide,
      lastServeConfidence: this.lastServeConfidence,
      rallyCount: this.rallyCount,
      endCount: this.endCount,
      missedEnds: this.missedEnds,
    };
  }

  get videoElement(): HTMLVideoElement | null {
    return this.video;
  }

  get streamObj(): MediaStream | null {
    return this.stream;
  }

  get loopStats(): CameraFrameStats {
    return this.tracker.snapshot();
  }

  /* ------------------------- frame processing ------------------------- */

  private onFrame(now: number): void {
    const video = this.video;
    if (!video || video.videoWidth === 0) return;
    const gray = this.grabGray(video);
    if (!gray) return;

    let total = 0;
    let bottomLeft = 0;
    let bottomRight = 0;

    if (this.prevGray) {
      const w = this.grayWidth;
      const h = this.grayHeight;
      const midX = Math.floor(w / 2);
      const bandTop = Math.floor(h * this.tuning.serviceBandTop);
      let sum = 0;
      let bl = 0;
      let br = 0;
      let nbl = 0;
      let nbr = 0;
      for (let y = 0; y < h; y++) {
        const inBand = y >= bandTop;
        const row = y * w;
        for (let x = 0; x < w; x++) {
          const i = row + x;
          const d = Math.abs(gray[i] - this.prevGray[i]);
          sum += d;
          if (x < midX) {
            if (inBand) { bl += d; nbl += 1; }
          } else if (inBand) {
            br += d; nbr += 1;
          }
        }
      }
      total = sum / (w * h);
      bottomLeft = bl / Math.max(1, nbl);
      bottomRight = br / Math.max(1, nbr);
    }
    this.prevGray = gray;
    this.motionEnergy = total;

    const t = this.tuning;
    const wasQuiet = total < t.quietThreshold;

    switch (this.state) {
      case "idle":
        this.accumulateApproach(bottomLeft, bottomRight, wasQuiet);
        if (total >= t.activeThreshold && now - this.lastEventTs >= t.minServeGapMs) {
          this.onServeDetected(now);
        }
        break;

      case "rally":
        if (wasQuiet) {
          if (this.quietSince === 0) this.quietSince = now;
          if (now - this.quietSince >= t.endDebounceMs && now - this.rallyStartTs >= t.minRallyMs) {
            this.onRallyEnded(now);
          }
        } else {
          this.quietSince = 0;
        }
        break;

      case "ended":
        this.accumulateApproach(bottomLeft, bottomRight, wasQuiet);
        if (total >= t.activeThreshold && now - this.lastEventTs >= t.minServeGapMs) {
          this.onServeDetected(now);
        }
        break;
    }
  }

  private accumulateApproach(bottomLeft: number, bottomRight: number, wasQuiet: boolean): void {
    // Only accumulate while mostly still; the approach is "who walks to the line".
    if (wasQuiet) return;
    this.approachLeft += bottomLeft;
    this.approachRight += bottomRight;
  }

  /* ------------------------- domain-event emission ------------------------- */

  private onServeDetected(now: number): void {
    const side = this.decideServeSide();
    const confidence = this.serveConfidence();
    const detId = this.nextDetectorId();

    // 1) If a previous rally is ended-but-unresolved, this serve is its check.
    //    The winning side of that rally is "who walks to the service line" now.
    if (this.state === "ended" && this.lastRallyDetectorId) {
      const winner = side ? this.teamOnSide(side) : null;
      if (winner) {
        this.emit({
          type: "RALLY_RESULT_CANDIDATE",
          detectorId: this.lastRallyDetectorId,
          winnerTeamId: winner,
          winnerPhysicalSide: side,
          reason: "ball_down_in_court",
          confidence,
          evidence: [`motion:serve_side=${side}`, `motion:conf=${confidence.toFixed(3)}`],
          ts: Date.now(),
        });
      }
    }

    // 2) The serve itself opens the next rally (first serve of a set never scores).
    this.emit({
      type: "SERVE_DETECTED",
      detectorId: detId,
      side,
      confidence,
      ts: Date.now(),
    });
    this.emit({ type: "RALLY_STARTED", detectorId: detId, ts: Date.now() });

    this.lastRallyDetectorId = detId;
    this.lastServeSide = side;
    this.lastServeConfidence = confidence;
    this.rallyStartTs = now;
    this.quietSince = 0;
    this.approachLeft = 0;
    this.approachRight = 0;
    this.lastEventTs = now;
    this.rallyCount += 1;
    this.state = "rally";
  }

  private onRallyEnded(now: number): void {
    if (!this.lastRallyDetectorId) return;
    this.emit({ type: "RALLY_ENDED", detectorId: this.lastRallyDetectorId, ts: Date.now() });
    this.endCount += 1;
    this.lastEventTs = now;
    this.approachLeft = 0;
    this.approachRight = 0;
    this.state = "ended";
  }

  /* ------------------------- side + confidence heuristics ------------------------- */

  private decideServeSide(): PhysicalSide | null {
    const l = this.approachLeft;
    const r = this.approachRight;
    if (l === 0 && r === 0) {
      // No approach signal yet: fall back to the calibrated team sides if known,
      // otherwise unknown (the engine will defer rather than guess).
      return this.cal?.teamsOnSides.left ? "left" : null;
    }
    return l >= r ? "left" : "right";
  }

  private serveConfidence(): number {
    const l = this.approachLeft;
    const r = this.approachRight;
    const total = l + r;
    if (total <= 0) return 0;
    const asym = Math.abs(l - r) / total; // 0 = ambiguous, 1 = clearly one side
    // Map asymmetry + evidence strength to 0..1. Weak, noisy side signals are
    // honestly reported low: this is what "defer + controle nodig" is for.
    const strength = Math.min(1, total / 400);
    return Math.round((0.4 + 0.55 * asym + 0.05 * strength) * 100) / 100;
  }

  private teamOnSide(side: PhysicalSide): TeamId | null {
    if (this.cal?.teamsOnSides[side]) return this.cal.teamsOnSides[side];
    return null;
  }

  private nextDetectorId(): string {
    this.seq += 1;
    return `motion-${this.seq}`;
  }

  private emit(ev: DomainEvent): void {
    this.sink?.(ev);
  }

  private notifyCameraLost(): void {
    this.state = "idle";
    this.stopTimer();
    this.cameraLostHandler?.();
  }

  /* ------------------------- pixel helpers ------------------------- */

  private grayWidth = 0;
  private grayHeight = 0;

  private grabGray(video: HTMLVideoElement): Float32Array | null {
    const w = this.tuning.processWidth;
    const h = Math.max(2, Math.round((video.videoHeight / video.videoWidth) * w));
    if (this.canvas!.width !== w) this.canvas!.width = w;
    if (this.canvas!.height !== h) this.canvas!.height = h;
    if (!this.ctx) return null;
    this.ctx.drawImage(video, 0, 0, w, h);
    let data: Uint8ClampedArray;
    try {
      data = this.ctx.getImageData(0, 0, w, h).data;
    } catch {
      // Tainted canvas (cross-origin video without CORS): cannot read pixels.
      return null;
    }
    const out = new Float32Array(w * h);
    for (let i = 0, p = 0; i < out.length; i++, p += 4) {
      out[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
    }
    this.grayWidth = w;
    this.grayHeight = h;
    return out;
  }
}
